import React, { useState, useMemo, useEffect } from "react";
import {
  CheckCircleOutlined,
  VisibilityOutlined,
  DeleteOutlined,
  ExpandMore,
  ChevronRight,
  ErrorOutline as ErrorOutlineIcon,
  PendingOutlined as PendingOutlineIcon,
  FiberManualRecord as FiberManualRecordIcon,
  EditOutlined
} from "@mui/icons-material";
import { ToastMessageUtil } from "common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "common/enums/controls/toast.enum";
import { ConfirmationModal } from "common/components";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "common/redux/core/root.reducer";
import {
  useGetBatchesPaginatedQuery,
  useGetGdxDocumentsPaginatedQuery
} from "../data/api/queries/processing-hub-queries";
import SimplePagination from "./SimplePagination";
import "./CollapsibleDocumentList.scss";

interface UploadedBy {
  initial: string;
  name: string;
  color: string;
}

interface DocumentData {
  id: string | number;
  documentGuid?: string;
  fileName: string;
  flowName?: string;
  docType?: string;
  dataExtracted?: string; // Legacy format (JSON string)
  extracteddata?: any[]; // New format (array of ExtractedDataField)
  uploadedDate: string;
  uploadedBy?: UploadedBy;
  activityStatus?: string;
  status?: string;
  updatedDate?: string; // Extraction date
  validation_status?: string; // Document status
  flowId?: string | number; // Keep flowId for data structure, but filtering is done at parent level
  batch_id?: string | null; // Added for linking to batch
  batch_name?: string; // Added for batch name grouping
}

interface GroupedDocuments {
  [key: string]: DocumentData[];
}

interface CollapsibleDocumentListProps {
  data?: DocumentData[];
  onView?: (id: string | number) => void;
  onDelete?: (id: string | number, documentGuid?: string) => void;
  onDownload?: (id: string | number) => void;
  isLoading?: boolean;
  error?: any;
  documentType?: string; // Optional since it's no longer used for logic, but kept for compatibility
  flowId?: number; // Added for fetching batches
  onRefresh?: () => void; // Added for refreshing batch data
  disableBatchQueries?: boolean;
}

const CollapsibleDocumentList: React.FC<CollapsibleDocumentListProps> = ({
  data = [],
  onView,
  onDelete,
  onDownload,
  isLoading = false,
  error = null,
  documentType = "Engineering",
  flowId = 0,
  onRefresh,
  disableBatchQueries = false
}) => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.user);

  // State for grouping option
  const [groupBy, setGroupBy] = useState<
    "status" | "uploadedDate" | "batch_name"
  >("status");

  // Separate state for batch-specific pagination - moved before API hooks to avoid TS errors
  const [batchPagination, setBatchPagination] = useState({
    page: 1,
    size: 10,
    currentBatchId: null as string | null,
    documents: [] as DocumentData[],
    totalPages: 1,
    totalRecords: 0,
    isLoading: false
  });

  // Fetch batches for batch name grouping
  const {
    data: batchesData,
    refetch: refetchBatches,
    isUninitialized: isBatchQueryUninitialized
  } = useGetBatchesPaginatedQuery(
    {
      page: 1,
      size: 10, // Get all batches for grouping
      sort: "start_time",
      dir: "desc",
      flow_id: flowId
    },
    { skip: disableBatchQueries || groupBy !== "batch_name" || flowId === 0 }
  );

  // Separate API query for batch-specific pagination
  const { data: batchDocumentsData, isLoading: isBatchLoading } =
    useGetGdxDocumentsPaginatedQuery(
      {
        page: batchPagination.page,
        flowConfigId: flowId,
        size: batchPagination.size,
        sort: "",
        dir: "desc",
        search: "",
        showDuplicates: false
      },
      {
        skip: disableBatchQueries || !batchPagination.currentBatchId,
        refetchOnMountOrArgChange: true
      }
    );

  // State for collapsed/expanded sections
  const [collapsedSections, setCollapsedSections] = useState<{
    [key: string]: boolean;
  }>({});

  // Mapping between group keys and batch IDs for batch expansion
  const [groupKeyToBatchId, setGroupKeyToBatchId] = useState<{
    [groupKey: string]: string;
  }>({});

  // Mapping between group keys and batch data for progress display
  const [groupKeyToBatchData, setGroupKeyToBatchData] = useState<{
    [groupKey: string]: any;
  }>({});

  // State for delete confirmation modal
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    documentId: string | number | null;
    documentName: string;
    documentGuid?: string;
    isDeleting: boolean;
  }>({
    isOpen: false,
    documentId: null,
    documentName: "",
    documentGuid: undefined,
    isDeleting: false
  });

  // Generate consistent color based on name
  const generateColorFromName = (name: string): string => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      const char = name.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    // Generate a bright, readable color
    const hue = Math.abs(hash) % 360;
    const saturation = 65 + (Math.abs(hash) % 20); // 65-85%
    const lightness = 45 + (Math.abs(hash) % 20); // 45-65%

    // Convert HSL to hex
    const hslToHex = (h: number, s: number, l: number): string => {
      l /= 100;
      const a = (s * Math.min(l, 1 - l)) / 100;
      const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color)
          .toString(16)
          .padStart(2, "0");
      };
      return `#${f(0)}${f(8)}${f(4)}`;
    };

    return hslToHex(hue, saturation, lightness);
  };

  // Helper function to get group key based on grouping option (for non-batch grouping)
  const getGroupKey = (document: DocumentData, grouping: string): string => {
    if (grouping === "status") {
      return document.status || "Unknown";
    } else if (grouping === "uploadedDate") {
      // Parse MM/DD/YYYY format to create a proper Date object
      const dateParts = document.uploadedDate.split("/");
      if (dateParts.length === 3) {
        const month = parseInt(dateParts[0], 10) - 1; // Month is 0-indexed in Date constructor
        const day = parseInt(dateParts[1], 10);
        const year = parseInt(dateParts[2], 10);
        const date = new Date(year, month, day);

        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric"
        });
      } else {
        return "Invalid Date";
      }
    } else {
      return "Unknown";
    }
  };

  // Group documents dynamically based on selected grouping option
  const groupedDocuments = useMemo(() => {
    const grouped: GroupedDocuments = {};
    const batchMapping: { [groupKey: string]: string } = {};
    const batchDataMapping: { [groupKey: string]: any } = {};

    if (groupBy === "batch_name" && batchesData?.items) {
      // Create groups for each batch
      batchesData.items.forEach((batch) => {
        const groupKey = `${batch.batch_name} (${batch.files_count} files, by ${batch.created_by})`;
        grouped[groupKey] = [];
        batchMapping[groupKey] = batch.id; // Map group key to batch ID
        batchDataMapping[groupKey] = batch; // Map group key to full batch data
      });

      // Add an "Unassigned" group for documents that don't belong to any batch
      grouped["Unassigned Documents"] = [];

      // Group documents by matching batch_id
      data.forEach((doc) => {
        if (doc.batch_id) {
          // Find the batch that matches this document's batch_id
          const matchingBatch = batchesData.items.find(
            (batch) => batch.id === doc.batch_id
          );
          if (matchingBatch) {
            const groupKey = `${matchingBatch.batch_name} (${matchingBatch.files_count} files, by ${matchingBatch.created_by})`;
            if (grouped[groupKey]) {
              grouped[groupKey].push(doc);
            }
          } else {
            // Document has batch_id but no matching batch found
            grouped["Unassigned Documents"].push(doc);
          }
        } else {
          // Document has no batch_id
          grouped["Unassigned Documents"].push(doc);
        }
      });

      // Remove empty unassigned group if no unassigned documents
      if (grouped["Unassigned Documents"].length === 0) {
        delete grouped["Unassigned Documents"];
      }
    } else {
      // Original grouping logic for status and uploadedDate
      data.forEach((doc) => {
        const groupKey = getGroupKey(doc, groupBy);

        if (!grouped[groupKey]) {
          grouped[groupKey] = [];
        }

        grouped[groupKey].push(doc);
      });
    }

    // Update the batch mapping states
    setGroupKeyToBatchId(batchMapping);
    setGroupKeyToBatchData(batchDataMapping);

    return grouped;
  }, [data, groupBy, batchesData]);

  // Initialize all sections as collapsed by default and reset when grouping changes
  useEffect(() => {
    const groupKeys = Object.keys(groupedDocuments);
    if (groupKeys.length > 0) {
      const initialCollapsedState: { [key: string]: boolean } = {};
      groupKeys.forEach((groupKey) => {
        initialCollapsedState[groupKey] = true; // true means collapsed
      });
      setCollapsedSections(initialCollapsedState);
    }
  }, [Object.keys(groupedDocuments).join(","), groupBy]); // Reset when grouping changes

  // Update batch pagination state when batch documents data changes
  useEffect(() => {
    if (batchDocumentsData && batchPagination.currentBatchId) {
      // Transform API response to match component interface
      const transformedDocuments = (batchDocumentsData.data || []).map(
        (apiDoc: any): DocumentData => ({
          id: apiDoc.id || apiDoc.document_id,
          documentGuid: apiDoc.id?.toString() || apiDoc.document_id?.toString(),
          fileName:
            apiDoc.document_name || apiDoc.original_filename || "Unknown File",
          flowName: apiDoc.config_name || apiDoc.flow_name,
          docType: apiDoc.document_type || apiDoc.config_name,
          dataExtracted: apiDoc.data_extracted,
          extracteddata: apiDoc.extracteddata, // New format
          uploadedDate:
            apiDoc.extracted_date ||
            apiDoc.uploaded_on ||
            apiDoc.uploaded_date ||
            "Unknown Date",
          uploadedBy: apiDoc.uploaded_by
            ? {
                initial: apiDoc.uploaded_by.charAt(0).toUpperCase(),
                name: apiDoc.uploaded_by,
                color: generateColorFromName(apiDoc.uploaded_by)
              }
            : undefined,
          status: apiDoc.status || apiDoc.extraction_status,
          updatedDate:
            apiDoc.updated_date || apiDoc.updatedDate || apiDoc.extracted_date,
          validation_status: apiDoc.validation_status,
          flowId: apiDoc.flow_id,
          batch_id: apiDoc.batch_id,
          batch_name: apiDoc.batch_name
        })
      );

      setBatchPagination((prev) => ({
        ...prev,
        documents: transformedDocuments,
        totalPages: batchDocumentsData.totalPages || 1,
        totalRecords: batchDocumentsData.totalRecords || 0,
        isLoading: isBatchLoading
      }));
    }
  }, [batchDocumentsData, isBatchLoading, batchPagination.currentBatchId]);

  // Handle refresh functionality
  useEffect(() => {
    if (onRefresh) {
      const handleRefreshBatches = () => {
        // Only refetch if the batch query is currently active (not skipped)
        // If the query is uninitialized, it means we're not in batch view,
        // so we don't need to refresh batch data
        if (
          groupBy === "batch_name" &&
          !isBatchQueryUninitialized &&
          flowId !== 0
        ) {
          refetchBatches();
        }
      };

      window.addEventListener("refreshBatches", handleRefreshBatches);

      return () => {
        window.removeEventListener("refreshBatches", handleRefreshBatches);
      };
    }
  }, [onRefresh, groupBy, refetchBatches, isBatchQueryUninitialized, flowId]);

  // Toggle section collapse/expand
  const toggleSection = (groupKey: string) => {
    const isCurrentlyCollapsed = collapsedSections[groupKey];
    const batchId = groupKeyToBatchId[groupKey];

    // If expanding a batch and we have a batch ID
    if (isCurrentlyCollapsed && groupBy === "batch_name" && batchId) {
      // Start batch-specific pagination
      setBatchPagination({
        page: 1,
        size: 10,
        currentBatchId: batchId,
        documents: [],
        totalPages: 1,
        totalRecords: 0,
        isLoading: true
      });
    } else if (!isCurrentlyCollapsed && groupBy === "batch_name" && batchId) {
      // Collapsing batch - clear the batch pagination
      setBatchPagination({
        page: 1,
        size: 10,
        currentBatchId: null,
        documents: [],
        totalPages: 1,
        totalRecords: 0,
        isLoading: false
      });
    }

    setCollapsedSections((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Handle page change for batch-specific pagination
  const handleBatchPageChange = (page: number) => {
    setBatchPagination((prev) => ({
      ...prev,
      page: page,
      isLoading: true
    }));
  };

  // Handle grouping selection

  // Delete confirmation handlers
  const handleDeleteClick = (
    documentId: string | number,
    documentName: string,
    documentGuid?: string
  ) => {
    setDeleteConfirmation({
      isOpen: true,
      documentId,
      documentName,
      documentGuid,
      isDeleting: false
    });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation({
      isOpen: false,
      documentId: null,
      documentName: "",
      documentGuid: undefined,
      isDeleting: false
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation.documentId) return;

    setDeleteConfirmation((prev) => ({ ...prev, isDeleting: true }));

    try {
      await onDelete?.(
        deleteConfirmation.documentId,
        deleteConfirmation.documentGuid
      );

      ToastMessageUtil.show(
        `Document "${deleteConfirmation.documentName}" has been deleted successfully`,
        ToastVariant.SUCCESS
      );

      // Close the confirmation modal first
      setDeleteConfirmation({
        isOpen: false,
        documentId: null,
        documentName: "",
        documentGuid: undefined,
        isDeleting: false
      });

      // Refresh after successful deletion
      if (onRefresh) {
        setTimeout(async () => {
          try {
            await onRefresh();
          } catch (refreshError) {
            console.error("Error during refresh:", refreshError);
          }
        }, 1000);
      }
    } catch (deleteError) {
      console.error("Error deleting document:", deleteError);

      ToastMessageUtil.show(
        `Failed to delete document "${deleteConfirmation.documentName}". Please try again.`,
        ToastVariant.ERROR
      );

      setDeleteConfirmation((prev) => ({ ...prev, isDeleting: false }));
    }
  };

  // Get badge color based on grouping type and value
  const getBadgeClass = (groupKey: string) => {
    if (groupBy === "status") {
      return getStatusBadgeClass(groupKey);
    } else if (groupBy === "uploadedDate") {
      return "status-date";
    } else {
      return "status-default";
    }
  };

  // Get status badge color (used when grouping by status)
  const getStatusBadgeClass = (status: string) => {
    const lowerStatus = status.toLowerCase();

    if (
      lowerStatus.includes("completed") ||
      lowerStatus.includes("finalized") ||
      lowerStatus.includes("approved")
    ) {
      return "status-completed";
    } else if (
      lowerStatus.includes("progress") ||
      lowerStatus.includes("queued") ||
      lowerStatus.includes("pending")
    ) {
      return "status-in-progress";
    } else if (
      lowerStatus.includes("error") ||
      lowerStatus.includes("failed") ||
      lowerStatus.includes("rejected")
    ) {
      return "status-error";
    } else if (
      lowerStatus.includes("new") ||
      lowerStatus.includes("uploaded")
    ) {
      return "status-new";
    } else {
      return "status-default";
    }
  };

  // Render uploaded by information
  const renderUploadedBy = (uploadedBy?: UploadedBy) => {
    if (!uploadedBy) {
      const displayName = currentUser.displayName || "Current User";
      const initial = displayName.charAt(0).toUpperCase();
      const color = generateColorFromName(displayName);

      return (
        <div className="uploaded-by">
          <div className="avatar" style={{ backgroundColor: color }}>
            {initial}
          </div>
          <span>{displayName}</span>
        </div>
      );
    }

    return (
      <div className="uploaded-by">
        <div className="avatar" style={{ backgroundColor: uploadedBy.color }}>
          {uploadedBy.initial}
        </div>
        <span>{uploadedBy.name}</span>
      </div>
    );
  };

  // Render document actions
  const renderActions = (document: DocumentData) => {
    const statusText = document.status || "";
    const lowerStatus = statusText.toLowerCase();
    // Normalize to ignore spacing/casing differences between raw ("ExtractionCompleted")
    // and formatted ("Extraction Completed") status values.
    const normalizedStatus = lowerStatus.replace(/\s+/g, "");

    const disabledStatuses = [
      "Extraction Queued",
      "Extraction in Progress",
      "Extraction Error"
    ];

    const canViewDocument = !disabledStatuses.some((disabledStatus) =>
      statusText.toLowerCase().includes(disabledStatus.toLowerCase())
    );

    // Knowledge graph generated -> view only (navigate to drawing intelligence)
    const isKnowledgeGraphGenerated =
      normalizedStatus.includes("knowledgegraph");
    // Extraction completed (but graph not yet generated) -> allow editing relationships
    const canEditRelationship =
      normalizedStatus.includes("extractioncompleted") &&
      !isKnowledgeGraphGenerated;

    const documentGuid = document.documentGuid || document.id;

    return (
      <div className="document-actions">
        <button
          className="action-btn view-btn"
          disabled={!canViewDocument}
          onClick={() => {
            if (!canViewDocument) return;
            if (isKnowledgeGraphGenerated) {
              navigate(`/drawing-intelligence/${documentGuid}`, {
                state: { path: "/processing-hub" }
              });
            } else if (onView) {
              onView(document.id);
            } else {
              navigate(`/documents/${documentGuid}?page=1&size=10`);
            }
          }}
          title={
            isKnowledgeGraphGenerated ? "View Knowledge Graph" : "View Document"
          }
        >
          <VisibilityOutlined />
        </button>
        <button
          className="action-btn edit-btn"
          disabled={!canEditRelationship}
          onClick={() => {
            if (canEditRelationship) {
              navigate(`/relationship-validation/${documentGuid}`, {
                state: {
                  path: "/processing-hub",
                  editMode: true
                }
              });
            }
          }}
          title="Edit Relationship"
        >
          <EditOutlined />
        </button>
        <button
          className="action-btn delete-btn"
          onClick={() =>
            handleDeleteClick(
              document.id,
              document.fileName,
              document.documentGuid
            )
          }
          title="Delete Document"
        >
          <DeleteOutlined />
        </button>
      </div>
    );
  };

  if (isLoading) {
    return <div className="collapsible-list-loading">Loading documents...</div>;
  }

  if (error) {
    return (
      <div className="collapsible-list-error">
        Error loading documents: {error.message}
      </div>
    );
  }

  if (Object.keys(groupedDocuments).length === 0) {
    return <div className="collapsible-list-empty">No documents found</div>;
  }

  return (
    <div className="collapsible-document-list">
      {/* Simple Grouping Selector */}
      <div className="grouping-controls">
        <label htmlFor="grouping-select">Group by:</label>
        <select
          id="grouping-select"
          value={groupBy}
          onChange={(e) =>
            setGroupBy(
              e.target.value as "status" | "uploadedDate" | "batch_name"
            )
          }
          className="grouping-select"
        >
          <option value="status">Status</option>
          <option value="uploadedDate">Upload Date</option>
          {/* <option value="batch_name">Batch Name</option> */}
        </select>
      </div>

      {/* Document Groups */}
      {Object.entries(groupedDocuments).map(([groupKey, documents]) => {
        const isCollapsed = collapsedSections[groupKey];
        const documentCount = documents.length;

        return (
          <div key={groupKey} className="status-section">
            {/* Group Header */}
            <div
              className="status-header"
              onClick={() => toggleSection(groupKey)}
            >
              <div className="status-header-left">
                <div className="collapse-icon">
                  {isCollapsed ? <ChevronRight /> : <ExpandMore />}
                </div>
                <div className="status-info">
                  <h3 className="status-title">{groupKey}</h3>
                  {/* <span className="document-count">
                    {documentCount} document{documentCount !== 1 ? "s" : ""}
                  </span> */}
                </div>
              </div>
              <div className="status-header-right">
                {groupBy === "batch_name" && groupKeyToBatchData[groupKey] ? (
                  // Show progress bar for batch grouping
                  <div className="batch-progress-right">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${Math.max(
                            groupKeyToBatchData[groupKey].progress_percentage ||
                              0,
                            2
                          )}%`,
                          height: "100%",
                          background: (() => {
                            const percentage =
                              groupKeyToBatchData[groupKey]
                                .progress_percentage || 0;
                            if (percentage >= 70) return "#28a745"; // Green
                            if (percentage >= 30) return "#007bff"; // Blue
                            return "#dc3545"; // Red
                          })(),
                          borderRadius: "5px",
                          transition: "width 0.3s ease",
                          display: "block"
                        }}
                      />
                    </div>
                    <span
                      className="progress-text"
                      style={{
                        color: (() => {
                          const percentage =
                            groupKeyToBatchData[groupKey].progress_percentage ||
                            0;
                          if (percentage >= 70) return "#28a745"; // Green
                          if (percentage >= 30) return "#007bff"; // Blue
                          return "#dc3545"; // Red
                        })()
                      }}
                    >
                      {Math.round(
                        groupKeyToBatchData[groupKey].progress_percentage || 0
                      )}
                      %
                    </span>
                  </div>
                ) : (
                  // Show status badge for other groupings
                  <span
                    className={`status-badge ${getStatusBadgeClass(groupKey)}`}
                  >
                    {groupKey}
                  </span>
                )}
              </div>
            </div>

            {/* Documents Table */}
            {!isCollapsed && (
              <div className="documents-table-container">
                <table className="documents-table">
                  <thead>
                    <tr>
                      <th>File name</th>
                      <th>Uploaded date</th>
                      <th>Uploaded by</th>
                      <th>Extraction status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Show loading state while batch documents are being fetched */}
                    {batchPagination.currentBatchId ===
                      groupKeyToBatchId[groupKey] && isBatchLoading ? (
                      <tr>
                        <td colSpan={5} className="loading-cell">
                          <div className="dash-loader">---</div>
                          <span>Loading batch documents...</span>
                        </td>
                      </tr>
                    ) : (
                      /* Show batch pagination documents if this batch is being paginated and loaded, otherwise show regular documents */
                      (batchPagination.currentBatchId ===
                        groupKeyToBatchId[groupKey] &&
                      batchPagination.documents.length > 0
                        ? batchPagination.documents
                        : documents
                      ).map((document) => (
                        <tr key={document.id} className="document-row">
                          <td className="file-name-cell">
                            {document.fileName}
                          </td>
                          <td className="uploaded-date-cell">
                            {document.uploadedDate}
                          </td>
                          <td className="uploaded-by-cell">
                            {renderUploadedBy(document.uploadedBy)}
                          </td>
                          <td className="status-cell">
                            <div className="status">
                              {document.status
                                ?.toLowerCase()
                                .includes("failed") ||
                              document.status
                                ?.toLowerCase()
                                .includes("error") ? (
                                <FiberManualRecordIcon
                                  className="check-icon"
                                  style={{ color: "red" }}
                                />
                              ) : document.status
                                  ?.toLowerCase()
                                  .includes("queued") ? (
                                <FiberManualRecordIcon
                                  className="check-icon"
                                  style={{ color: "#ff9500" }}
                                />
                              ) : (
                                <CheckCircleOutlined className="check-icon" />
                              )}
                              <span>{document.status || "Unknown"}</span>
                            </div>
                          </td>
                          <td className="actions-cell">
                            {renderActions(document)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {/* Pagination for this specific batch */}
                {batchPagination.currentBatchId ===
                  groupKeyToBatchId[groupKey] &&
                  batchPagination.totalPages > 1 && (
                    <div className="batch-pagination-wrapper">
                      <SimplePagination
                        currentPage={batchPagination.page}
                        totalPages={batchPagination.totalPages}
                        onPageChange={handleBatchPageChange}
                      />
                    </div>
                  )}
              </div>
            )}
          </div>
        );
      })}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        title="Delete Document"
        message={`Are you sure you want to delete "${deleteConfirmation.documentName}"? This action cannot be undone.`}
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isLoading={deleteConfirmation.isDeleting}
      />
    </div>
  );
};

export default CollapsibleDocumentList;
